import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CaretLeft, DownloadSimple, ArrowClockwise, CheckCircle, XCircle, Archive, PaperPlaneTilt,
  ClockCounterClockwise, MagicWand, CopySimple, Cpu, CurrencyDollar, Timer, ImageSquare,
  ArrowsClockwise, Sparkle,
} from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MediaImage } from "../components/MediaImage";
import { creativeApi, fetchMediaMap, mediaUrl, pollJobUntilDone } from "../useCreative";
import { fmtDateTime, fmtCost } from "../creativeUtils";

export default function AssetDetail() {
  const { assetId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);       // details payload
  const [timeline, setTimeline] = useState(null);
  const [dests, setDests] = useState([]);
  const [mediaMap, setMediaMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [dims, setDims] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [selectedDests, setSelectedDests] = useState([]);

  const load = useCallback(async () => {
    const [details, tl, destinations, media] = await Promise.all([
      creativeApi.getAssetDetails(assetId),
      creativeApi.getTimeline(assetId).catch(() => null),
      creativeApi.listDestinations().catch(() => []),
      fetchMediaMap(),
    ]);
    setD(details); setTimeline(tl); setDests(destinations); setMediaMap(media);
  }, [assetId]);
  useEffect(() => { load(); }, [load]);

  const runAction = async (fn, label) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(label); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doPublish = async () => {
    if (selectedDests.length === 0) return toast.error("Select at least one destination");
    await runAction(() => creativeApi.publish({ asset_id: assetId, destination_ids: selectedDests }), "Publish requested");
    setPublishOpen(false); setSelectedDests([]);
  };

  const remix = () => {
    nav("/creative/generate", { state: { remix: {
      structured: d.prompt.structured || {},
      negative_prompt: d.prompt.negative_prompt,
      seed: d.prompt.seed,
      aspect_ratio: d.asset.aspect_ratio,
      parent_asset_id: d.asset.id,
    } } });
  };

  const duplicate = async () => {
    if (!window.confirm("Regenerate an exact copy of this creative? This starts a new generation.")) return;
    setBusy(true);
    try {
      const brief = {
        kind: d.asset.kind || "image", operation: "generate",
        prompt: d.prompt.rendered || "", negative_prompt: d.prompt.negative_prompt || null,
        aspect_ratio: d.asset.aspect_ratio || null, style: d.generation.brief?.style || null,
        params: { structured: d.prompt.structured || {}, parent_asset_id: d.asset.id },
      };
      let job = await creativeApi.generate(brief, `dup-${Date.now()}`);
      if (["queued", "running"].includes(job.status)) job = await pollJobUntilDone(job.id);
      if (job.status !== "completed") { toast.error(job.error || `Generation ${job.status}`); return; }
      const assets = await creativeApi.listAssets();
      const made = assets.find((a) => a.job_id === job.id);
      toast.success("Exact copy generated");
      if (made) nav(`/creative/assets/${made.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="p-8 text-sm text-muted-foreground" data-testid="asset-detail-loading">Loading asset…</div>;

  const a = d.asset;
  const g = d.generation;
  const p = d.prompt;
  const routing = g.routing || {};
  const contentUrl = mediaUrl(mediaMap, a.media_asset_id);
  const resolution = (a.width && a.height) ? `${a.width}×${a.height}` : (dims ? `${dims.width}×${dims.height}` : "—");
  const s = a.status;
  const enabledDests = dests.filter((x) => x.enabled !== false);

  return (
    <div className="space-y-6" data-testid="asset-detail">
      <Link to="/creative/assets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="asset-back">
        <CaretLeft size={14} /> All assets
      </Link>

      <PromoPageHeader
        overline="Creative Studio · Asset"
        title={`${a.kind} · ${a.operation}`}
        description={p.rendered || "Generated creative asset."}
        actions={<StatusBadge status={a.status} data-testid="asset-detail-status" />}
      />

      {/* Operational action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-4" data-testid="asset-action-bar">
        <ActionBtn onClick={remix} tone="ai" Icon={MagicWand} testid="asset-remix">Remix</ActionBtn>
        <ActionBtn onClick={duplicate} busy={busy} tone="muted" Icon={CopySimple} testid="asset-duplicate">Duplicate</ActionBtn>
        {contentUrl && (
          <a href={contentUrl} target="_blank" rel="noreferrer" download
             className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent" data-testid="asset-download">
            <DownloadSimple size={15} /> Download
          </a>
        )}
        <div className="flex-1" />
        {(s === "draft" || s === "rejected") && (
          <ActionBtn onClick={() => runAction(() => creativeApi.approveAsset(a.id), "Asset approved")} busy={busy} tone="success" Icon={CheckCircle} testid="asset-approve">Approve</ActionBtn>
        )}
        {(s === "draft" || s === "approved") && (
          <ActionBtn onClick={() => runAction(() => creativeApi.rejectAsset(a.id), "Asset rejected")} busy={busy} tone="destructive" Icon={XCircle} testid="asset-reject">Reject</ActionBtn>
        )}
        {s === "approved" && (
          <ActionBtn onClick={() => setPublishOpen((v) => !v)} busy={busy} tone="brand" Icon={PaperPlaneTilt} testid="asset-publish">Publish</ActionBtn>
        )}
        {s !== "archived" && (
          <ActionBtn onClick={() => runAction(() => creativeApi.archiveAsset(a.id), "Asset archived")} busy={busy} tone="muted" Icon={Archive} testid="asset-archive">Archive</ActionBtn>
        )}
      </div>

      {publishOpen && (
        <div className="rounded-lg border border-brand/30 bg-brand/5 p-4" data-testid="publish-panel">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-brand"><PaperPlaneTilt size={15} weight="fill" /> Publish to destinations</div>
          {enabledDests.length === 0 ? <p className="text-xs text-muted-foreground">No destinations yet. Add one on the Publishing page.</p> : (
            <div className="space-y-1.5" data-testid="destination-selector">
              {enabledDests.map((x) => (
                <label key={x.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50" data-testid={`dest-option-${x.id}`}>
                  <input type="checkbox" checked={selectedDests.includes(x.id)} onChange={(e) => setSelectedDests((prev) => e.target.checked ? [...prev, x.id] : prev.filter((y) => y !== x.id))} className="h-4 w-4 rounded border-border accent-brand" />
                  <span className="flex-1">{x.destination_name || x.destination_id}</span><span className="text-xs text-muted-foreground">{x.provider}</span>
                </label>
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => { setPublishOpen(false); setSelectedDests([]); }} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent" data-testid="publish-cancel">Cancel</button>
            <button onClick={doPublish} disabled={busy || enabledDests.length === 0} className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50" data-testid="publish-confirm">Send publish request</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Preview */}
        <div className="overflow-hidden rounded-lg border border-border bg-background/40" data-testid="asset-preview">
          {a.kind === "video" ? (
            <video src={contentUrl} controls className="w-full object-contain" data-testid="asset-video-player" />
          ) : (
            <MediaImage url={contentUrl} className="w-full object-contain" onLoaded={setDims} />
          )}
        </div>

        {/* Generation + metadata */}
        <div className="space-y-4">
          <Section title="Generation" Icon={Cpu} testid="asset-generation">
            <Meta Icon={Cpu} label="Provider" value={g.provider || "—"} />
            <Meta Icon={Cpu} label="Model" value={g.model || "—"} />
            <Meta Icon={CurrencyDollar} label="Generation cost" value={fmtCost(g.cost)} />
            <Meta Icon={Timer} label="Generation time" value={g.generation_time_ms != null ? `${(g.generation_time_ms / 1000).toFixed(2)}s` : "—"} />
            {a.kind === "video" && (
              <Meta Icon={Timer} label="Duration" value={g.duration_seconds != null ? `${g.duration_seconds}s` : (a.duration_seconds != null ? `${a.duration_seconds}s` : "—")} />
            )}
            <Meta Icon={ImageSquare} label="Resolution" value={resolution} />
            <Meta Icon={ImageSquare} label="Aspect ratio" value={a.aspect_ratio || "—"} />
            <Meta Icon={Sparkle} label="Seed" value={p.seed != null ? String(p.seed) : "—"} />
            <Meta Icon={ClockCounterClockwise} label="Created" value={fmtDateTime(a.created_at)} />
          </Section>

          <Section title="Routing Decision" Icon={ArrowsClockwise} testid="asset-routing">
            {g.routing ? (
              <div className="space-y-2 text-sm">
                <Meta label="Selected provider" value={routing.selected_provider || g.provider || "—"} />
                <Meta label="Policy" value={routing.policy || "—"} />
                <Meta label="Capability" value={routing.capability || "—"} />
                {routing.reason && <p className="text-xs text-muted-foreground">{routing.reason}</p>}
                {Array.isArray(routing.fallback_chain) && routing.fallback_chain.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    <span className="text-muted-foreground">Fallback chain:</span>
                    {routing.fallback_chain.map((c, i) => (
                      <span key={i} className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px]">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground">No routing explanation recorded for this asset.</p>}
          </Section>
        </div>
      </div>

      {/* Prompts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Full Prompt" Icon={Sparkle} testid="asset-full-prompt">
          <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">{p.rendered || "—"}</pre>
          {p.negative_prompt && (
            <div className="mt-3"><div className="promo-overline mb-1">Negative prompt</div>
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs">{p.negative_prompt}</pre></div>
          )}
        </Section>
        <Section title="Structured Prompt" Icon={MagicWand} testid="asset-structured-prompt">
          {p.structured && Object.keys(p.structured).length > 0 ? (
            <dl className="grid grid-cols-1 gap-1.5 text-sm sm:grid-cols-2">
              {Object.entries(p.structured).filter(([k]) => k !== "extra").map(([k, v]) => (
                <div key={k} className="rounded-md border border-border bg-background/40 px-2.5 py-1.5" data-testid={`structured-${k}`}>
                  <dt className="promo-overline">{k.replace(/_/g, " ")}</dt>
                  <dd className="truncate text-xs">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                </div>
              ))}
            </dl>
          ) : <p className="text-sm text-muted-foreground">No structured prompt stored.</p>}
        </Section>
      </div>

      {/* Generation History (lineage + lifecycle timeline) */}
      <Section title="Generation History" Icon={ClockCounterClockwise} testid="asset-history">
        {(d.lineage || []).length > 0 && (
          <div className="mb-4 space-y-2" data-testid="asset-lineage">
            <div className="promo-overline">Lineage</div>
            {d.lineage.map((l) => (
              <button key={l.id} onClick={() => nav(`/creative/assets/${l.id}`)} className="flex w-full items-center gap-3 rounded-md border border-border bg-background/40 p-2 text-left hover:border-ai/40" data-testid={`lineage-${l.id}`}>
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded"><MediaImage url={mediaUrl(mediaMap, l.media_asset_id, "thumbnail")} className="h-full w-full object-cover" /></div>
                <div className="min-w-0 flex-1"><div className="text-xs font-medium capitalize">{l.relation}</div><div className="text-[11px] text-muted-foreground">{fmtDateTime(l.created_at)}</div></div>
                <StatusBadge status={l.status} dot={false} />
              </button>
            ))}
          </div>
        )}
        <div className="promo-overline mb-2">Lifecycle timeline</div>
        {!timeline || timeline.approval_history?.length === 0 ? (
          <p className="text-xs text-muted-foreground">No lifecycle events yet.</p>
        ) : (
          <ol className="relative space-y-3 border-l border-border pl-4">
            {timeline.approval_history.map((h, i) => (
              <li key={i} className="relative" data-testid={`timeline-entry-${i}`}>
                <span className={cn("absolute -left-[21px] top-1 h-2 w-2 rounded-full", DOT_FOR(h.action))} />
                <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium capitalize">{h.action.replace("_", " ")}</span><span className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</span></div>
                {h.actor_email && <p className="text-[11px] text-muted-foreground">{h.actor_email}</p>}
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}

function Section({ title, Icon, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Icon size={15} className="text-ai" /> {title}</h3>
      {children}
    </section>
  );
}
function Meta({ Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 py-1.5 text-sm last:border-0">
      <span className="flex items-center gap-1.5 text-muted-foreground">{Icon && <Icon size={13} />} {label}</span>
      <span className="truncate text-right font-medium">{value}</span>
    </div>
  );
}
function ActionBtn({ onClick, busy, tone, Icon, children, testid }) {
  const tones = {
    ai: "bg-ai text-ai-foreground hover:opacity-90",
    success: "bg-success text-success-foreground hover:opacity-90",
    destructive: "bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20",
    brand: "bg-brand text-brand-foreground hover:opacity-90",
    muted: "border border-border text-muted-foreground hover:bg-accent hover:text-foreground",
  };
  return (
    <button onClick={onClick} disabled={busy} className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50", tones[tone])} data-testid={testid}>
      <Icon size={15} weight="bold" /> {children}
    </button>
  );
}
function DOT_FOR(action) {
  if (action === "approved" || action === "published") return "bg-success";
  if (action === "rejected") return "bg-destructive";
  if (action === "archived") return "bg-muted-foreground";
  return "bg-brand";
}
